/*
 * Hollis CRIB configurator UI. Markup + JSON payload: snippets/crib-configurator.liquid.
 * Preview stage: snippets/crib-preview.liquid. Pure logic: assets/crib-core.js (window.CribCore).
 */
(function () {
  'use strict';
  var Core = window.CribCore;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(c) { return '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function deltaText(d) { return d === null ? 'Unavailable' : (d > 0 ? '+' + money(d) : 'Included'); }
  function copy(o) { var r = {}; for (var k in o) r[k] = o[k]; return r; }

  function escAttr(s) {
    s = String(s);
    if (typeof window.CSS !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return s.replace(/(["\\])/g, '\\$1');
  }

  // Compute a CSS selector (scoped to stepsEl) that will re-find the given focused element
  // after the next render(), or null if it isn't one we track.
  function focusSelectorFor(el, stepsEl) {
    if (!el || !stepsEl || !stepsEl.contains(el)) return null;
    if (el.hasAttribute('data-crib-ack')) return '[data-crib-ack]';
    if (el.hasAttribute('data-crib-set')) {
      var key = el.getAttribute('data-crib-set');
      if (key.indexOf('addon:') === 0) return '[data-crib-set="' + escAttr(key) + '"]';
      var val = el.getAttribute('data-value');
      return '[data-crib-set="' + escAttr(key) + '"][data-value="' + escAttr(val) + '"]';
    }
    if (el.hasAttribute('data-crib-toggle')) return '[data-crib-toggle="' + escAttr(el.getAttribute('data-crib-toggle')) + '"]';
    if (el.hasAttribute('data-crib-open')) return '[data-crib-toggle="' + escAttr(el.getAttribute('data-crib-open')) + '"]';
    return null;
  }

  // Mobile "pinned preview": while [data-crib] is on screen (max-width: 1023px), pins the
  // preview stage under the fixed site header via document.body.classList 'crib-pin' (CSS in
  // assets/crib.css). Feature-detects matchMedia/IntersectionObserver and no-ops safely without
  // them (e.g. jsdom in tests) — no pinning ever happens, which is the safe default.
  function setupMobilePreviewPin(root, preview) {
    if (!window.matchMedia) return;
    var mql = window.matchMedia('(max-width: 1023px)');
    var observer = null;

    function fixedHeaderEl() {
      var el = document.getElementById('main-header');
      if (el) return el;
      var header = document.querySelector('.header');
      var node = header;
      while (node && node !== document.body && node.nodeType === 1) {
        var pos = window.getComputedStyle ? window.getComputedStyle(node).position : '';
        if (pos === 'fixed') return node;
        node = node.parentElement;
      }
      return header;
    }

    function updateOffsets() {
      var headerEl = fixedHeaderEl();
      var topPx = headerEl ? headerEl.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--crib-top', topPx + 'px');
      var zone = 0;
      if (preview) {
        var stage = preview.querySelector('[data-crib-stage]');
        var bar = preview.querySelector('.crib-preview__bar');
        if (stage) zone += stage.getBoundingClientRect().height;
        if (bar) zone += bar.getBoundingClientRect().height;
      }
      document.documentElement.style.setProperty('--crib-preview-zone', zone + 'px');
      return topPx + zone;
    }

    function teardownObserver() {
      if (observer) { observer.disconnect(); observer = null; }
      document.body.classList.remove('crib-pin');
    }

    function setupObserver() {
      teardownObserver();
      if (!window.IntersectionObserver) return; // no-op fallback: never pins
      var margin = updateOffsets();
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          // Short viewports (landscape phones) don't have room for a pinned preview + steps + total bar.
          document.body.classList.toggle('crib-pin', entries[i].isIntersecting && window.innerHeight >= 560);
        }
      }, { rootMargin: '-' + Math.round(margin) + 'px 0px 0px 0px', threshold: 0 });
      observer.observe(root);
    }

    function onModeChange() {
      if (mql.matches) setupObserver(); else teardownObserver();
    }

    onModeChange();
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onModeChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onModeChange);

    window.addEventListener('resize', function () { if (mql.matches) updateOffsets(); });
    window.addEventListener('orientationchange', function () { if (mql.matches) updateOffsets(); });
  }

  function showInitError(root, err) {
    if (window.console) window.console.warn('[CRIB] init failed:', err);
    var stepsEl = root.querySelector('[data-crib-steps]');
    if (stepsEl) stepsEl.innerHTML = '<p>Our builder hit a snag. Call (805) 967-4456 and we\u2019ll build your rig with you.</p>';
    var primaryEl = root.querySelector('[data-crib-primary]');
    if (primaryEl) primaryEl.hidden = true;
  }

  function init(root) {
    try {
      initInner(root);
    } catch (err) {
      showInitError(root, err);
    }
  }

  function initInner(root) {
    var data = JSON.parse(root.querySelector('[data-crib-json]').textContent);
    var cfg = data.config, prices = data.prices, rig = data.rig;
    var problems = Core.validate(cfg);
    if (problems.length) {
      if (window.console) window.console.warn('[CRIB] config problems:', problems);
      // Never sell a misconfigured rig — show the same call-us fallback as an init error
      // instead of rendering a builder we can't trust the pricing/availability of.
      showInitError(root, new Error('invalid config: ' + problems.join('; ')));
      return;
    }
    var stepsEl = root.querySelector('[data-crib-steps]');
    var totalEl = root.querySelector('[data-crib-total]');
    var primary = root.querySelector('[data-crib-primary]');
    var preview = document.querySelector('[data-crib-preview]');
    var ids = cfg.steps.map(function (s) { return s.id; }).concat(['review']);
    var params = new URLSearchParams(window.location.search);
    var openInit = {};
    openInit[ids[0]] = true;
    var state = {
      sel: Core.decodeBuild(cfg, params.get('build')) || Core.defaultSelection(cfg),
      open: openInit, ack: false, error: '', busy: false, angle: 'front'
    };
    // Tracks whichever step header (or the review toggle) was most recently opened/closed by the
    // shopper, purely so render()'s focus-restore fallback has a sensible place to land focus when
    // the element the shopper was focused on is gone after a re-render (multiple steps can be open
    // at once now, so "the" open step no longer identifies a single element).
    var lastToggleId = ids[0];

    // Only rewrite the URL (history.replaceState) once the user has actually changed a
    // selection — not on the initial render, so loading a plain product URL (or one restored
    // from ?build=) doesn't immediately rewrite it.
    var selChanged = false;

    // Size chart dialog (Elite 2 harness sizing). Built once from cfg.size_chart and appended
    // directly to `root` — NOT inside stepsEl — so re-renders (which only rewrite
    // stepsEl.innerHTML) never tear it down while it's open. Native <dialog>.showModal() gives
    // us focus trapping, Esc-to-close and a backdrop for free; browsers without it fall back to
    // a manually-trapped, manually-closed overlay.
    var chartDialog = null, lastFocusedBeforeChart = null, chartActiveUnit = 'in';
    if (cfg.size_chart) {
      window.__cribChartSeq = (window.__cribChartSeq || 0) + 1;
      var chartTitleId = 'crib-chart-title-' + window.__cribChartSeq;
      chartDialog = document.createElement('dialog');
      chartDialog.className = 'crib-chart';
      chartDialog.setAttribute('data-crib-chart', '');
      chartDialog.setAttribute('aria-labelledby', chartTitleId);
      chartDialog.innerHTML = buildChartHtml(cfg.size_chart, chartTitleId);
      chartDialog.addEventListener('close', function () { restoreChartFocus(); });
      chartDialog.addEventListener('click', function (e) { if (e.target === chartDialog) closeChartDialog(); });
      root.appendChild(chartDialog);
    }

    function buildChartHtml(sizeChart, titleId) {
      var unitIds = ['in', 'cm'].filter(function (u) { return sizeChart.units[u]; });
      var unitButtons = unitIds.map(function (u) {
        return '<button type="button" class="crib-chart__unit' + (u === 'in' ? ' is-active' : '') + '" data-crib-chart-unit="' + u +
          '" aria-pressed="' + (u === 'in') + '">' + esc(u) + '</button>';
      }).join('');
      var tables = unitIds.map(function (u) {
        var t = sizeChart.units[u];
        return '<table class="crib-chart__table" data-crib-chart-table="' + u + '"' + (u === 'in' ? '' : ' hidden') + '>' +
          '<thead><tr>' + t.headers.map(function (h) { return '<th scope="col">' + esc(h) + '</th>'; }).join('') + '</tr></thead>' +
          '<tbody>' + t.rows.map(function (r) {
            return '<tr>' + r.map(function (c, i) { return i === 0 ? '<th scope="row">' + esc(c) + '</th>' : '<td>' + esc(c) + '</td>'; }).join('') + '</tr>';
          }).join('') + '</tbody></table>';
      }).join('');
      return '<div class="crib-chart__head"><h2 id="' + esc(titleId) + '" class="crib-chart__title">' + esc(sizeChart.title) + '</h2>' +
        '<button type="button" class="crib-chart__close" data-crib-chart-close aria-label="Close size chart">×</button></div>' +
        '<div class="crib-chart__units" role="group" aria-label="Units">' + unitButtons + '</div>' +
        '<div class="crib-chart__scroll">' + tables + '</div>' +
        (sizeChart.note ? '<p class="crib-chart__note">' + esc(sizeChart.note) + '</p>' : '');
    }

    function restoreChartFocus() {
      if (lastFocusedBeforeChart && typeof lastFocusedBeforeChart.focus === 'function') lastFocusedBeforeChart.focus();
      lastFocusedBeforeChart = null;
    }

    function onChartFallbackKeydown(e) {
      var key = e.key || '';
      if (key === 'Escape' || e.keyCode === 27) { closeChartDialog(); return; }
      if (key === 'Tab' || e.keyCode === 9) {
        var focusables = chartDialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!focusables.length) return;
        var first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    function openChartDialog() {
      if (!chartDialog) return;
      lastFocusedBeforeChart = document.activeElement;
      if (typeof chartDialog.showModal === 'function') {
        try { chartDialog.showModal(); return; } catch (e) { /* fall through to manual fallback */ }
      }
      chartDialog.setAttribute('open', '');
      chartDialog.setAttribute('data-crib-chart-fallback', '');
      document.addEventListener('keydown', onChartFallbackKeydown, true);
      var closeBtn = chartDialog.querySelector('[data-crib-chart-close]');
      if (closeBtn) closeBtn.focus();
    }

    function closeChartDialog() {
      if (!chartDialog) return;
      if (chartDialog.hasAttribute('data-crib-chart-fallback')) {
        chartDialog.removeAttribute('open');
        chartDialog.removeAttribute('data-crib-chart-fallback');
        document.removeEventListener('keydown', onChartFallbackKeydown, true);
        restoreChartFocus();
      } else if (typeof chartDialog.close === 'function') {
        chartDialog.close(); // fires the 'close' listener above, which restores focus
      }
    }

    function setChartUnit(u) {
      if (!chartDialog) return;
      chartActiveUnit = u;
      var tables = chartDialog.querySelectorAll('[data-crib-chart-table]');
      for (var i = 0; i < tables.length; i++) tables[i].hidden = tables[i].getAttribute('data-crib-chart-table') !== u;
      var buttons = chartDialog.querySelectorAll('[data-crib-chart-unit]');
      for (var j = 0; j < buttons.length; j++) {
        var on = buttons[j].getAttribute('data-crib-chart-unit') === u;
        buttons[j].classList.toggle('is-active', on);
        buttons[j].setAttribute('aria-pressed', on);
      }
    }

    function deltas(key, values) { return Core.optionDeltas(cfg, state.sel, prices, rig.price, key, values); }
    function evalNow() { return Core.evaluate(cfg, state.sel, prices, rig.price); }
    function update(patch) { for (var k in patch) state[k] = patch[k]; render(); }
    function setSel(key, value) {
      var next = copy(state.sel);
      if (value === false) delete next[key]; else next[key] = value;
      selChanged = true;
      update({ sel: Core.normalize(cfg, next), error: '' });
    }

    // Any number of steps (including 'review') can be open at once — state.open is a set
    // (id -> true) rather than a single id. These helpers always work from a fresh copy so a
    // single update() call can both close one step and open another in one render.
    function cloneOpen() { return copy(state.open); }
    function openStep(id) {
      var o = cloneOpen(); o[id] = true;
      lastToggleId = id;
      update({ open: o });
    }
    function closeStep(id) {
      var o = cloneOpen(); delete o[id];
      lastToggleId = id;
      update({ open: o });
    }
    function toggleStep(id) {
      if (state.open[id]) closeStep(id); else openStep(id);
    }
    function gotoStep(curId, nextId) {
      var o = cloneOpen();
      delete o[curId];
      o[nextId] = true;
      lastToggleId = nextId;
      update({ open: o });
    }

    function stepSummary(step) {
      var lines = Core.summary(cfg, state.sel);
      if (step.type === 'choice') return lines.filter(function (l) { return l[0] === step.label; })[0][1];
      if (step.type === 'colors') return step.groups.map(function (g) { return g.label + ' ' + Core.colorLabel(state.sel[g.id]); }).join(' · ');
      if (step.type === 'addons') return lines.filter(function (l) { return l[0] === step.label; })[0][1];
      return '';
    }

    function optionButton(key, value, label, sub, d, selected, extraClass) {
      return '<button type="button" class="crib-opt' + (extraClass || '') + (selected ? ' is-selected' : '') + '"' +
        ' data-crib-set="' + esc(key) + '" data-value="' + esc(value) + '" aria-pressed="' + selected + '"' + (d.ok ? '' : ' disabled') + '>' +
        (extraClass === ' crib-opt--check' ? '<span class="crib-opt__box" aria-hidden="true"></span>' : '') +
        '<span class="crib-opt__text"><span class="crib-opt__label">' + esc(label) + '</span>' +
        (sub ? '<span class="crib-opt__sub">' + esc(sub) + '</span>' : '') + '</span>' +
        '<span class="crib-opt__price">' + deltaText(d.delta) + '</span></button>';
    }

    function swatches(group) {
      var d = deltas(group.id, group.colors);
      return '<div class="crib-colors__group"><p class="crib-colors__label"><strong>' + esc(group.label) + '</strong> — ' +
        esc(Core.colorLabel(state.sel[group.id])) + '</p><div class="crib-colors__row" role="group" aria-label="' + esc(group.label) + ' color">' +
        group.colors.map(function (c) {
          var sel = state.sel[group.id] === c, extra = d[c].delta > 0 ? ' (+' + money(d[c].delta) + ')' : '';
          return '<button type="button" class="crib-swatch crib-swatch--' + esc(c) + (sel ? ' is-selected' : '') + '" style="--crib-sw:' +
            esc(Core.COLORS[c] ? Core.COLORS[c].hex : '#ccc') + '" data-crib-set="' + esc(group.id) + '" data-value="' + esc(c) +
            '" aria-pressed="' + sel + '" aria-label="' + esc(Core.colorLabel(c) + extra) + '" title="' + esc(Core.colorLabel(c) + extra) + '"' +
            (d[c].ok ? '' : ' disabled') + '></button>';
        }).join('') + '</div></div>';
    }

    function stepBody(step, i) {
      var html = '';
      if (step.type === 'choice') {
        var d = deltas(step.id, step.options.map(function (o) { return o.id; }));
        html += step.options.map(function (o) { return optionButton(step.id, o.id, o.label, o.sub, d[o.id], state.sel[step.id] === o.id); }).join('');
        var cur = step.options.filter(function (o) { return o.id === state.sel[step.id]; })[0];
        var hasSizedOption = step.options.some(function (o) { return !!o.sizes; });
        if (cur && cur.sizes) {
          var ds = deltas('size', cur.sizes);
          var showChartBtn = !!(cfg.size_chart && cfg.size_chart.for === cur.id);
          html += '<div class="crib-sizes"><p class="crib-colors__label"><strong>' + esc(cur.label) + ' size</strong>' +
            (showChartBtn ? ' <button type="button" class="crib-size-chart-link" data-crib-open-chart>Size chart</button>' : '') +
            '</p><div class="crib-sizes__row" role="group" aria-label="Size">' +
            cur.sizes.map(function (z) {
              var on = state.sel.size === z;
              return '<button type="button" class="crib-size' + (on ? ' is-selected' : '') + '" data-crib-set="size" data-value="' + esc(z) + '" aria-pressed="' + on + '"' + (ds[z].ok ? '' : ' disabled') + '>' + esc(z) + '</button>';
            }).join('') + '</div></div>';
        } else if (cur && hasSizedOption) {
          html += '<p class="crib-size-note">One size fits all.</p>';
        }
      } else if (step.type === 'colors') {
        html += step.groups.map(swatches).join('');
      } else if (step.type === 'addons') {
        step.options.forEach(function (o) {
          var key = 'addon:' + o.id, on = !!state.sel[key];
          var d = deltas(key, [false, true]);
          // A selected add-on must always be removable, even if it's since gone unavailable —
          // only turning an unavailable add-on ON is blocked.
          var dUse = on ? { ok: true, delta: d[true].delta } : d[true];
          html += optionButton(key, on ? 'off' : 'on', o.label, o.sub, dUse, on, ' crib-opt--check');
          if (on && o.color_group) html += '<div class="crib-subgroup">' + swatches(o.color_group) + '</div>';
        });
      }
      var next = ids[i + 1];
      var nextStepLabel = next === 'review' ? 'Review' : cfg.steps[i + 1].label;
      return html + '<button type="button" class="crib-next--link" data-crib-open="' + esc(next) + '" data-crib-current="' + esc(step.id) + '">' +
        esc('Next: ' + nextStepLabel + ' →') + '</button>';
    }

    function reviewBody() {
      var e = evalNow();
      var rows = Core.summary(cfg, state.sel).map(function (l) {
        return '<li class="crib-review__row"><span>' + esc(l[0]) + '</span><span>' + esc(l[1]) + '</span></li>';
      }).join('');
      var priceRows = '<li class="crib-review__row"><span>Base build</span><span>' + money(rig.price) + '</span></li>' +
        e.lines.map(function (l) { return '<li class="crib-review__row"><span>' + esc(l.label) + '</span><span>+' + money(l.price) + '</span></li>'; }).join('');
      return '<ul class="crib-review">' + rows + '</ul><ul class="crib-review crib-review--prices">' + priceRows +
        '<li class="crib-review__row crib-review__row--total"><span>Total</span><span>' + money(e.total) + '</span></li></ul>' +
        '<label class="crib-ack"><input type="checkbox" data-crib-ack' + (state.ack ? ' checked' : '') + '> <span>' + esc(data.ack_text) + '</span></label>' +
        (state.error ? '<p class="crib-error" role="alert">' + esc(state.error) + '</p>' : '');
    }

    function stepItem(id, n, label, summary, body) {
      var open = !!state.open[id];
      return '<li class="crib-step' + (open ? ' is-open' : '') + '">' +
        '<button type="button" class="crib-step__head" data-crib-toggle="' + esc(id) + '" aria-expanded="' + open + '">' +
        '<span class="crib-step__num">' + n + '</span><span class="crib-step__text"><span class="crib-step__label">' + esc(label) +
        '</span><span class="crib-step__summary">' + esc(summary) + '</span></span>' +
        '<span class="crib-step__edit">' + (open ? '' : 'Edit') + '</span></button>' +
        (open ? '<div class="crib-step__body">' + body + '</div>' : '') + '</li>';
    }

    // Preview swaps preload every new layer src before anything on screen changes — this avoids
    // the flash of the fallback (an all-black product shot) that happens if we tear down the
    // current layer <img>s and show the fallback while the new images are still loading.
    // `previewDesiredKey` always holds the srcs-key renderPreview most recently decided it wants
    // on screen (or null for "show the fallback"); any in-flight preload whose key no longer
    // matches it when it finishes is stale and is discarded rather than applied.
    var previewDesiredKey = null;
    var previewPreloadingKey = null;

    function renderPreview() {
      if (!preview) return;
      var stage = preview.querySelector('[data-crib-stage]');
      var fallback = stage.querySelector('[data-crib-fallback]');
      var layers = Core.renderLayers(cfg, state.sel, state.angle);
      var srcsKey = layers ? layers.join('|') : null;
      previewDesiredKey = srcsKey;
      if (srcsKey === null) {
        renderFallback();
      } else if (srcsKey === stage.getAttribute('data-crib-failed')) {
        // This exact combo already failed to load — keep showing the fallback,
        // don't re-add (and re-fail) the same broken image layers every render.
      } else if (stage.getAttribute('data-crib-srcs') !== srcsKey && previewPreloadingKey !== srcsKey) {
        previewPreloadingKey = srcsKey;
        preloadLayers(stage, fallback, layers, srcsKey);
      }
      preview.querySelectorAll('[data-crib-angle]').forEach(function (b) {
        var on = b.getAttribute('data-crib-angle') === state.angle;
        b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on);
      });
      var chips = preview.querySelector('[data-crib-chips]');
      chips.innerHTML = Core.colorGroups(cfg).filter(function (cg) { return state.sel[cg.group.id]; }).map(function (cg) {
        var c = state.sel[cg.group.id];
        return '<span class="crib-chip"><span class="crib-chip__dot crib-swatch--' + esc(c) + '" style="--crib-sw:' + esc(Core.COLORS[c] ? Core.COLORS[c].hex : '#ccc') + '"></span>' +
          esc(cg.group.label + ' ' + Core.colorLabel(c)) + '</span>';
      }).join('');
      renderIdeas();
    }

    // The current selection "matches" an idea when every colour group the idea names agrees with
    // the live selection — groups the idea doesn't mention (e.g. LX2 on a curated mix) aren't
    // considered, since the idea never claimed an opinion about them.
    function ideaMatches(idea) {
      for (var k in idea.colors) { if (state.sel[k] !== idea.colors[k]) return false; }
      return true;
    }

    // Renders the "Color ideas" thumbnail strip under the preview stage. Each thumbnail stacks
    // the same layer URLs the main preview uses (Core.renderLayers), just with the idea's colours
    // applied to the *current* build (wing/harness/plate/bands/add-ons untouched) — so the
    // thumbnail always reflects what tapping it would actually produce. Hidden entirely when the
    // current wing/plate/harness combo has no render at all, or the config has no ideas.
    function renderIdeas() {
      var ideasEl = preview.querySelector('[data-crib-ideas]');
      if (!ideasEl) return;
      var row = ideasEl.querySelector('[data-crib-ideas-row]');
      var currentLayers = Core.renderLayers(cfg, state.sel, 'front');
      var list = currentLayers ? Core.ideas(cfg) : [];
      if (!list.length) { ideasEl.hidden = true; row.innerHTML = ''; return; }
      ideasEl.hidden = false;
      var activeEl = document.activeElement;
      var activeId = (activeEl && row.contains(activeEl) && activeEl.hasAttribute('data-crib-idea')) ?
        activeEl.getAttribute('data-crib-idea') : null;
      row.innerHTML = list.map(function (idea) {
        var ideaSel = Core.applyIdea(cfg, state.sel, idea);
        var layers = Core.renderLayers(cfg, ideaSel, 'front') || [];
        var selected = ideaMatches(idea);
        var imgs = layers.map(function (src) {
          return '<img src="' + esc(src) + '" alt="" loading="lazy" decoding="async" width="1200" height="1200">';
        }).join('');
        return '<button type="button" class="crib-idea' + (selected ? ' is-selected' : '') + '" data-crib-idea="' + esc(idea.id) +
          '" aria-pressed="' + selected + '" aria-label="' + esc(idea.label) + '" title="' + esc(idea.label) + '">' +
          '<span class="crib-idea__stage">' + imgs + '</span></button>';
      }).join('');
      if (activeId) {
        var toFocus = row.querySelector('[data-crib-idea="' + escAttr(activeId) + '"]');
        if (toFocus) toFocus.focus();
      }
    }

    // A hidden pool of plain <img> elements used purely to preload srcs. Not styled/visible and
    // never the ones shown on stage, so swapping them in doesn't touch what's on screen.
    function preloadPool(stage) {
      var pool = stage.querySelector('[data-crib-preload-pool]');
      if (!pool) {
        pool = document.createElement('div');
        pool.setAttribute('data-crib-preload-pool', '');
        pool.hidden = true;
        stage.appendChild(pool);
      }
      return pool;
    }

    function preloadLayers(stage, fallback, srcs, srcsKey) {
      if (!srcs.length) { settle(); return; }
      var pool = preloadPool(stage);
      var remaining = srcs.length, failed = false;
      // Track only the <img> elements *this* preload call added — the pool can briefly hold
      // more than one in-flight preload's elements at once (e.g. a second combo superseding the
      // first before it settles), so a settling call must clean up only its own elements, never
      // the whole pool, or it can delete a different, still-pending preload's images out from
      // under it.
      var myImgs = srcs.map(function (src, i) {
        var img = document.createElement('img');
        img.setAttribute('data-crib-preload', String(i));
        img.onload = settle;
        img.onerror = function () { failed = true; settle(); };
        img.src = src;
        pool.appendChild(img);
        return img;
      });
      function settle() {
        remaining -= 1;
        if (remaining > 0) return;
        myImgs.forEach(function (img) { if (img.parentNode) img.parentNode.removeChild(img); });
        if (previewPreloadingKey === srcsKey) previewPreloadingKey = null;
        // The user moved on to a different combo/angle/color while this was loading — discard.
        if (srcsKey !== previewDesiredKey) return;
        if (failed) { renderFallback(srcsKey); return; }
        applyLayers(stage, fallback, srcs, srcsKey);
      }
    }

    // Everything in `srcs` has already finished loading (via preloadLayers) — reuse the existing
    // layer <img> elements where possible (just flipping their already-cached src, so nothing
    // flickers), create new ones if the layer count grew, and drop any that are no longer needed.
    function applyLayers(stage, fallback, srcs, srcsKey) {
      stage.removeAttribute('data-crib-failed');
      var imgs = stage.querySelectorAll('.crib-preview__layer');
      for (var i = 0; i < srcs.length; i++) {
        var img = imgs[i];
        if (!img) {
          img = document.createElement('img');
          img.className = 'crib-preview__layer';
          img.width = 1200; img.height = 1200;
          attachLayerErrorHandler(img, srcsKey);
          stage.appendChild(img);
        }
        // Base layer describes the build (wing/plate/harness + colours); the stacked layers are decorative.
        img.alt = i === 0 ? Core.previewAlt(cfg, state.sel) : '';
        if (img.src !== srcs[i]) img.src = srcs[i];
      }
      for (var j = imgs.length - 1; j >= srcs.length; j--) {
        imgs[j].onerror = null;
        if (imgs[j].parentNode) imgs[j].parentNode.removeChild(imgs[j]);
      }
      if (fallback) fallback.hidden = true;
      stage.setAttribute('data-crib-srcs', srcsKey);
    }
    function attachLayerErrorHandler(img, srcsKey) {
      img.onerror = function () { if (img.parentNode) renderFallback(srcsKey); };
    }
    function removeLayers(stage) {
      stage.querySelectorAll('.crib-preview__layer').forEach(function (n) {
        n.onerror = null;
        n.parentNode.removeChild(n);
      });
    }
    function renderFallback(failedKey) {
      var stage = preview.querySelector('[data-crib-stage]');
      removeLayers(stage);
      var pool = stage.querySelector('[data-crib-preload-pool]');
      if (pool) pool.innerHTML = '';
      stage.removeAttribute('data-crib-srcs');
      if (failedKey) stage.setAttribute('data-crib-failed', failedKey); else stage.removeAttribute('data-crib-failed');
      var fb = stage.querySelector('[data-crib-fallback]');
      if (fb) fb.hidden = false;
    }

    function scrollStepIntoView(id) {
      // Mobile-only: on desktop there's no pinned preview to clear, and matchMedia/jsdom-less
      // environments already no-op via the guard below.
      if (!window.matchMedia || !window.matchMedia('(max-width: 1023px)').matches) return;
      var head = stepsEl.querySelector('[data-crib-toggle="' + escAttr(id) + '"]');
      if (head && typeof head.scrollIntoView === 'function') head.scrollIntoView({ block: 'start' });
    }

    function render() {
      var activeEl = document.activeElement;
      var wasFocusInSteps = !!(activeEl && stepsEl.contains(activeEl));
      var focusSel = focusSelectorFor(activeEl, stepsEl);
      stepsEl.innerHTML = cfg.steps.map(function (s, i) { return stepItem(s.id, i + 1, s.label, stepSummary(s), stepBody(s, i)); }).join('') +
        stepItem('review', cfg.steps.length + 1, 'Review', money(evalNow().total), reviewBody());
      var totalText = money(evalNow().total);
      if (totalEl.textContent !== totalText) totalEl.textContent = totalText;
      if (!rig.available) {
        primary.textContent = 'Currently unavailable — call (805) 967-4456';
        primary.disabled = true;
      } else {
        primary.textContent = state.busy ? 'Adding…' : (state.open.review ? 'Add to cart' : 'Review build');
        primary.disabled = state.busy;
      }
      renderPreview();
      var refocused = false;
      if (focusSel) {
        try {
          var toFocus = stepsEl.querySelector(focusSel);
          if (toFocus && !toFocus.disabled) { toFocus.focus(); refocused = true; }
        } catch (e) { /* bad selector — ignore, focus is best-effort */ }
      }
      if (!refocused && wasFocusInSteps) {
        // Remembered focus target is gone or disabled — don't let focus silently drop to body;
        // land it on the header of whichever step was most recently toggled open/closed (falling
        // back to the review toggle if that one isn't in the DOM for some reason).
        var openToggle = stepsEl.querySelector('[data-crib-toggle="' + escAttr(lastToggleId) + '"]') ||
          stepsEl.querySelector('[data-crib-toggle="review"]');
        if (openToggle) openToggle.focus();
      }
      if (selChanged) {
        var p = new URLSearchParams(window.location.search);
        p.set('build', Core.encodeBuild(state.sel));
        window.history.replaceState(null, '', window.location.pathname + '?' + p.toString() + window.location.hash);
      }
    }

    function addToCart() {
      if (!rig.available) return;
      if (!state.ack) return update({ error: 'Please check the box to confirm you understand this rig is built to order.' });
      if (evalNow().missing.length) return update({ error: 'One of your choices is unavailable right now — please call us at (805) 967-4456.' });
      update({ busy: true, error: '' });
      var items = Core.cartItems(cfg, state.sel, prices, rig.id, Core.newBuildId());
      if (items === null) return update({ busy: false, error: 'One of your choices is unavailable right now — please call us at (805) 967-4456.' });
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return fetch('/cart.js'); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          update({ busy: false });
          if (window.CartJS && typeof CartJS.getCart === 'function') CartJS.getCart();
          if (typeof window.refreshMiniCart === 'function') window.refreshMiniCart(cart); else window.location.href = '/cart';
        })
        .catch(function () { update({ busy: false, error: 'Something went wrong adding your build. Please try again or call (805) 967-4456.' }); });
    }

    root.addEventListener('click', function (e) {
      var chartTarget = e.target.closest('[data-crib-open-chart],[data-crib-chart-close],[data-crib-chart-unit]');
      if (chartTarget) {
        if (chartTarget.hasAttribute('data-crib-open-chart')) openChartDialog();
        else if (chartTarget.hasAttribute('data-crib-chart-close')) closeChartDialog();
        else setChartUnit(chartTarget.getAttribute('data-crib-chart-unit'));
        return;
      }
      var t = e.target.closest('[data-crib-toggle],[data-crib-set],[data-crib-open],[data-crib-primary]');
      if (!t || t.disabled) return;
      if (t.hasAttribute('data-crib-toggle')) {
        var id = t.getAttribute('data-crib-toggle');
        var opening = !state.open[id];
        toggleStep(id);
        if (opening) scrollStepIntoView(id);
      }
      else if (t.hasAttribute('data-crib-open')) {
        var openId = t.getAttribute('data-crib-open');
        var curId = t.getAttribute('data-crib-current');
        gotoStep(curId, openId);
        scrollStepIntoView(openId);
      }
      else if (t.hasAttribute('data-crib-primary')) {
        if (state.open.review) addToCart();
        else { openStep('review'); scrollStepIntoView('review'); }
      }
      else {
        var key = t.getAttribute('data-crib-set'), val = t.getAttribute('data-value');
        if (key.indexOf('addon:') === 0) setSel(key, val === 'on' ? true : false); else setSel(key, val);
      }
    });
    root.addEventListener('change', function (e) {
      if (e.target.hasAttribute('data-crib-ack')) {
        state.ack = e.target.checked;
        state.error = '';
        if (e.target.checked) {
          var err = root.querySelector('.crib-error');
          if (err && err.parentNode) err.parentNode.removeChild(err);
        }
      }
    });
    if (preview) preview.addEventListener('click', function (e) {
      var b = e.target.closest('[data-crib-angle]');
      if (b) { update({ angle: b.getAttribute('data-crib-angle') }); return; }
      var ideaBtn = e.target.closest('[data-crib-idea]');
      if (ideaBtn) {
        var idea = Core.ideas(cfg).filter(function (i) { return i.id === ideaBtn.getAttribute('data-crib-idea'); })[0];
        if (idea) { selChanged = true; update({ sel: Core.applyIdea(cfg, state.sel, idea), error: '' }); }
      }
    });

    render();
    setupMobilePreviewPin(root, preview);
  }

  function start() { document.querySelectorAll('[data-crib]').forEach(function (el) { if (el.querySelector('[data-crib-json]')) init(el); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
